import { parseDocumentReferenceHref, parseLineReferenceHref } from './entity-reference.js';

/**
 * A tiny, dependency-free Markdown → HTML renderer for the reviewer's own
 * supporting note (HS2-9K33CQ). It is deliberately minimal (headings, bold,
 * italic, inline code, links, unordered/ordered lists, paragraphs, line breaks)
 * and escapes all HTML first, so author-typed text cannot inject markup. The
 * result is trusted HTML the caller wraps in kerf's `raw()`.
 *
 * This is not a full CommonMark implementation; it covers the common note-taking
 * subset and leaves anything else as escaped text.
 */

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Inline spans: code, bold, italic, links. Operates on already-escaped text. */
function renderInline(escaped: string): string {
  let out = escaped;
  // Inline code first so its contents are not further formatted.
  out = out.replace(/`([^`]+)`/g, (_m, code: string) => `<code>${code}</code>`);
  // Links [text](url) — external http(s)/mailto plus the tightly constrained,
  // demand-qualified line reference used by notes and chat. Other schemes stay text.
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text: string, url: string) => {
    if (/^(https?:\/\/|mailto:)/i.test(url))
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    const line = parseLineReferenceHref(url);
    if (line !== undefined)
      return `<a href="${url}" class="entity-reference" data-demand-reference="${line.demandId}" data-line-reference="${line.lineId}">${text}</a>`;
    const document = parseDocumentReferenceHref(url);
    if (document !== undefined)
      return `<a href="${url}" class="entity-reference" data-demand-reference="${document.demandId}" data-document-reference="${document.documentId}">${text}</a>`;
    return text;
  });
  out = out.replace(/\*\*([^*]+)\*\*/g, (_m, b: string) => `<strong>${b}</strong>`);
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, (_m, pre: string, i: string) => `${pre}<em>${i}</em>`);
  return out;
}

/** Render a Markdown string to trusted HTML (already escaped). */
export function renderMarkdown(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const blocks: string[] = [];
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushPara = (): void => {
    if (para.length > 0) {
      blocks.push(`<p>${para.map((l) => renderInline(escapeHtml(l))).join('<br>')}</p>`);
      para = [];
    }
  };
  const flushList = (): void => {
    if (list !== null) {
      const tag = list.ordered ? 'ol' : 'ul';
      blocks.push(`<${tag}>${list.items.map((it) => `<li>${renderInline(escapeHtml(it))}</li>`).join('')}</${tag}>`);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    const ul = /^[-*]\s+(.*)$/.exec(line);
    const ol = /^\d+\.\s+(.*)$/.exec(line);
    if (line.trim() === '') {
      flushPara();
      flushList();
    } else if (heading !== null) {
      flushPara();
      flushList();
      const level = (heading[1] ?? '').length;
      blocks.push(`<h${String(level)}>${renderInline(escapeHtml(heading[2] ?? ''))}</h${String(level)}>`);
    } else if (ul !== null) {
      flushPara();
      if (list === null || list.ordered) {
        flushList();
        list = { ordered: false, items: [] };
      }
      list.items.push(ul[1] ?? '');
    } else if (ol !== null) {
      flushPara();
      if (list === null || !list.ordered) {
        flushList();
        list = { ordered: true, items: [] };
      }
      list.items.push(ol[1] ?? '');
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return blocks.join('\n');
}
